/**
 * User Identity Utility
 * 
 * Handles the mapping between different user ID formats:
 * - authId: The ID from the authentication system (e.g., Stytch)
 * - apiId: The ID used in the backend API/database
 */

import { config } from '@/config/config';
import { authLogger } from '@/core/utils/logger';
import { getStorageItem, setStorageItem, removeStorageItem, STORAGE_KEYS } from '@/core/utils/local-storage';
import { extractErrorMessage } from '@/shared/utils/error';

interface UserIdMapping {
  authId: string;
  apiId: string;
}

// In-memory cache of the mapping to avoid constant localStorage reads
let cachedMapping: UserIdMapping | null = null;

// Enable debug mode in development only
const DEBUG_MODE = config.features.debugLogging && false;

// Helper for conditional logging
const debugLog = (message: string, data?: unknown) => {
  if (!DEBUG_MODE) return;
  if (data) {
    authLogger.debug(`[userIdentity] ${message}`, data);
  } else {
    authLogger.debug(`[userIdentity] ${message}`);
  }
};

/**
 * Initialize the user identity system
 * Call this as early as possible in app lifecycle
 */
export const initUserIdentity = (): void => {
  // Try to load mapping from localStorage
  try {
    const mapping = getStorageItem(STORAGE_KEYS.USER_ID_MAPPING) as UserIdMapping | null;
    if (mapping) {
      cachedMapping = mapping;
      debugLog('Initialized with mapping:', cachedMapping);
    } else {
      debugLog('No existing mapping found in localStorage');
    }
  } catch (error: unknown) {
    const errorDetails = {
      message: extractErrorMessage(error),
    };
    authLogger.error('[userIdentity] Error initializing:', errorDetails);
  }

  // Debug logging for diagnosing issues
  debugLog('Initialization complete, cached mapping:', cachedMapping);
};

/**
 * Gets the user ID mapping from localStorage
 */
export const getUserIdMapping = (): UserIdMapping | null => {
  // Return cached mapping if available
  if (cachedMapping) {
    return cachedMapping;
  }

  try {
    const mapping = getStorageItem(STORAGE_KEYS.USER_ID_MAPPING) as UserIdMapping | null;
    debugLog('Reading mapping from localStorage:', mapping);

    if (mapping) {
      cachedMapping = mapping; // Update cache
      debugLog('Parsed mapping:', mapping);
      return mapping;
    }
  } catch (error: unknown) {
    const errorDetails = {
      message: extractErrorMessage(error),
    };
    authLogger.error('[userIdentity] Error parsing user ID mapping:', errorDetails);
  }

  debugLog('No mapping found');
  return null;
};

/**
 * Sets the user ID mapping in localStorage
 */
export const setUserIdMapping = (authId: string, apiId: string): void => {
  if (!authId || !apiId) return;

  try {
    // First check if we already have this mapping to avoid unnecessary updates
    if (cachedMapping && cachedMapping.authId === authId && cachedMapping.apiId === apiId) {
      debugLog('Mapping already set, skipping update');
      return;
    }

    const mapping: UserIdMapping = { authId, apiId };
    setStorageItem(STORAGE_KEYS.USER_ID_MAPPING, mapping);
    cachedMapping = mapping; // Update cache

    debugLog(`Set mapping: ${authId} ↔ ${apiId}`);
  } catch (error: unknown) {
    const errorDetails = {
      message: extractErrorMessage(error),
    };
    authLogger.error('[userIdentity] Error setting user ID mapping:', errorDetails);
  }
};

/**
 * Clears the user ID mapping (useful during logout)
 */
export const clearUserIdMapping = (): void => {
  removeStorageItem(STORAGE_KEYS.USER_ID_MAPPING);
  cachedMapping = null; // Clear cache
  debugLog('Cleared mapping');
};

/**
 * Checks if a given sender ID corresponds to the current user
 * by checking against both possible IDs
 */
export const isCurrentUser = (senderId: string | null | undefined): boolean => {
  if (!senderId) return false;
  
  // Special case - explicit currentUser marker
  if (senderId === 'currentUser' || senderId === 'current-user') return true;
  
  // Get the ID mapping
  const mapping = getUserIdMapping();
  
  // Debug to see what we're checking against
  const logMessageId = Math.random() < 0.5; // Log 50% for debugging
  if (DEBUG_MODE && logMessageId) {
    authLogger.debug('[userIdentity] isCurrentUser check:', {
      senderId,
      mapping,
      hasMapping: !!mapping,
      authIdMatch: mapping ? senderId === mapping.authId : false,
      apiIdMatch: mapping ? senderId === mapping.apiId : false,
    });
  }

  if (!mapping) {
    // Try to recover mapping from localStorage if cache is empty
    try {
      const recovered = getStorageItem(STORAGE_KEYS.USER_ID_MAPPING) as UserIdMapping | null;
      if (recovered) {
        cachedMapping = recovered;
        if (DEBUG_MODE) {
          authLogger.debug('[userIdentity] Recovered mapping from localStorage:', recovered);
        }
        return senderId === recovered.authId || senderId === recovered.apiId;
      }
    } catch (e) {
      authLogger.error('[userIdentity] Failed to recover mapping:', e);
    }
    
    if (DEBUG_MODE && logMessageId) {
      authLogger.debug('[userIdentity] No mapping found for user identity check');
    }
    return false;
  }
  
  // Check against both IDs
  const matches = senderId === mapping.authId || senderId === mapping.apiId;
  
  // Increased logging for debugging
  if (DEBUG_MODE && (matches || logMessageId)) {
    authLogger.debug(`[userIdentity] ID "${senderId}" ${matches ? 'MATCHES' : 'does NOT match'} current user (auth=${mapping.authId}, api=${mapping.apiId})`);
  }
  
  return matches;
};

/**
 * Gets the API ID for the current user
 */
export const getCurrentUserApiId = (): string | null => {
  const mapping = getUserIdMapping();
  return mapping?.apiId || null;
};

/**
 * Canonical current user ID for the app (backend/database ID).
 * Prefer using this everywhere in UI/business logic for comparisons.
 */
export const getCurrentUserId = (): string | null => {
  return getCurrentUserApiId();
};

/**
 * Resolves the current user's backend (API) ID with sensible fallbacks.
 * Prefer mapping (localStorage), then user.apiId, then user.id.
 */
export const resolveCurrentUserApiId = (
  user?: { id?: string; apiId?: string }
): string | null => {
  return getCurrentUserApiId() || user?.apiId || user?.id || null;
};

/**
 * Gets the Auth ID for the current user
 */
export const getCurrentUserAuthId = (): string | null => {
  const mapping = getUserIdMapping();
  return mapping?.authId || null;
};

/**
 * Translates an auth ID to an API ID
 */
export const authIdToApiId = (authId: string): string | null => {
  const mapping = getUserIdMapping();
  if (mapping && mapping.authId === authId) {
    return mapping.apiId;
  }
  return null;
};

/**
 * Translates an API ID to an auth ID
 */
export const apiIdToAuthId = (apiId: string): string | null => {
  const mapping = getUserIdMapping();
  if (mapping && mapping.apiId === apiId) {
    return mapping.authId;
  }
  return null;
}; 

/**
 * Robust ownership check for team messages.
 * - Uses stored auth/api mapping when available
 * - Falls back to currentUserId prop when provided
 * - For DMs, if mapping is missing and we only know the partner id, assume
 *   any message not from the partner is from the current user (best-effort)
 */
export const isMyTeamMessage = (
  rawSenderId: string | null | undefined,
  options?: { currentUserId?: string | null; dmPartnerId?: string | null }
): boolean => {
  if (!rawSenderId) return false;
  const senderId = typeof rawSenderId === 'string' ? rawSenderId.trim() : String(rawSenderId || '').trim();
  if (!senderId) return false;

  // Explicit current user markers
  if (senderId === 'currentUser' || senderId === 'current-user') return true;

  const mapping = getUserIdMapping();
  const candidates: string[] = [];
  if (mapping) {
    candidates.push(mapping.authId, mapping.apiId);
  }
  const currentUserIdRaw = options?.currentUserId ? String(options.currentUserId).trim() : '';
  // Ignore placeholder values so DM fallback can apply while mapping is missing
  const currentUserId = (currentUserIdRaw === 'current-user' || currentUserIdRaw === 'currentUser') ? '' : currentUserIdRaw;
  if (currentUserId && !candidates.includes(currentUserId)) candidates.push(currentUserId);

  // If we can definitively compare, do so now
  if (candidates.length > 0) {
    return candidates.includes(senderId);
  }

  // DM fallback when we lack any identity candidates
  const partnerId = options?.dmPartnerId ? String(options.dmPartnerId).trim() : '';
  if (partnerId) {
    return senderId !== partnerId;
  }

  return false;
};